import { useQuery } from '@tanstack/react-query';
import { CalendarCheck, FileText, Undo2, Workflow } from 'lucide-react';

import { api } from '@/lib/api';
import type { RelatorioVolume } from '@/features/relatorios/tipos';
import { formatarInteiro, proporcao } from '@/features/relatorios/formato';
import {
  DEFINICOES_STATUS,
  ROTULOS_PRIORIDADE,
  ROTULOS_TIPO,
  STATUS,
  type StatusSolicitacao,
} from '@/features/solicitacoes/status';
import { GraficoBarras } from '@/components/graficos/barras';
import { GraficoRosca } from '@/components/graficos/rosca';
import { GraficoSerie } from '@/components/graficos/serie';
import { ROTULOS_ORIGEM } from '@/features/solicitacoes/origem';
import { CartaoGrafico, Indicador } from './cartoes';
import { EsqueletoBloco } from './esqueleto-bloco';
import { ErroBloco } from './erro-bloco';

/**
 * Cor da barra de cada status.
 *
 * Usa o token de **texto** do status, não o de fundo: o fundo é claro por ser
 * feito para receber texto por cima, e uma barra nele desapareceria contra o
 * cartão.
 */
const CORES_STATUS: Record<StatusSolicitacao, string> = {
  ENVIADO: 'bg-status-enviado-texto',
  LIDO_PELA_REGULACAO: 'bg-status-lido-texto',
  PENDENCIA_DOCUMENTACAO: 'bg-status-pendencia-texto',
  EM_PROCESSO: 'bg-status-processo-texto',
  AGENDADO: 'bg-status-agendado-texto',
  DEVOLVIDO: 'bg-status-devolvido-texto',
  CANCELADO: 'bg-status-cancelado-texto',
};

/** Média diária com uma casa, que é a precisão que a pergunta comporta. */
const MEDIA = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

export function BlocoVolume({ consulta }: { consulta: string }) {
  const { data, isPending, error } = useQuery({
    queryKey: ['relatorio', 'volume', consulta],
    queryFn: () => api.get<RelatorioVolume>(`/relatorios/volume?${consulta}`),
  });

  if (isPending) return <EsqueletoBloco />;
  if (error) return <ErroBloco erro={error} />;

  const porStatus = new Map(data.porStatus.map((linha) => [linha.status, linha.total]));
  const agendadas = porStatus.get('AGENDADO') ?? 0;
  const devolvidas = porStatus.get('DEVOLVIDO') ?? 0;
  const emAberto = data.total - agendadas - devolvidas;

  // `?? 0` porque uma instalação antiga pode responder sem o campo enquanto o
  // backend não subiu — e a tela não deve quebrar por isso.
  const temDemandaReprimida =
    (data.porOrigem?.find((linha) => linha.origem === 'DEMANDA_REPRIMIDA')?.total ?? 0) > 0;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Indicador
          rotulo="Solicitações no período"
          valor={formatarInteiro(data.total)}
          descricao={`${MEDIA.format(data.total / data.periodo.dias)} por dia, em média`}
          icone={FileText}
          tom="bg-marca-suave text-marca"
        />
        <Indicador
          rotulo="Agendadas"
          valor={formatarInteiro(agendadas)}
          descricao={proporcao(agendadas, data.total)}
          icone={CalendarCheck}
          tom="bg-status-agendado-fundo text-status-agendado-texto"
        />
        <Indicador
          rotulo="Devolvidas"
          valor={formatarInteiro(devolvidas)}
          descricao={proporcao(devolvidas, data.total)}
          icone={Undo2}
          tom="bg-status-devolvido-fundo text-status-devolvido-texto"
        />
        <Indicador
          rotulo="Ainda em fluxo"
          valor={formatarInteiro(emAberto)}
          descricao="Nem agendadas nem devolvidas"
          icone={Workflow}
          tom="bg-status-enviado-fundo text-status-enviado-texto"
        />
      </div>

      <CartaoGrafico
        titulo="Evolução no período"
        descricao={
          data.serie.granularidade === 'mes'
            ? 'Agrupado por mês — o período pedido passa de três meses.'
            : 'Solicitações registradas por dia.'
        }
      >
        <GraficoSerie pontos={data.serie.pontos} granularidade={data.serie.granularidade} />
      </CartaoGrafico>

      <div className="grid gap-5 lg:grid-cols-2">
        <CartaoGrafico titulo="Solicitações por UBS" descricao="Quem mais encaminhou no período.">
          <GraficoBarras
            itens={data.porUbs.map((linha) => ({
              chave: linha.ubsId,
              rotulo: linha.nome,
              valor: linha.total,
              detalhe: proporcao(linha.total, data.total),
            }))}
          />
        </CartaoGrafico>

        <CartaoGrafico
          titulo="Situação atual"
          descricao="Onde está cada solicitação registrada no período, hoje."
        >
          <GraficoBarras
            itens={STATUS.map((status) => ({
              chave: status,
              rotulo: DEFINICOES_STATUS[status].rotulo,
              valor: porStatus.get(status) ?? 0,
              cor: CORES_STATUS[status],
            }))}
          />
        </CartaoGrafico>

        <CartaoGrafico titulo="Por tipo">
          <GraficoRosca
            unidade="solicitações"
            fatias={data.porTipo.map((linha) => ({
              chave: linha.tipo,
              rotulo: ROTULOS_TIPO[linha.tipo],
              valor: linha.total,
            }))}
          />
        </CartaoGrafico>

        <CartaoGrafico titulo="Por prioridade">
          <GraficoRosca
            unidade="solicitações"
            fatias={data.porPrioridade.map((linha) => ({
              chave: linha.prioridade,
              rotulo: ROTULOS_PRIORIDADE[linha.prioridade],
              valor: linha.total,
            }))}
          />
        </CartaoGrafico>

        {/* Só quando há demanda reprimida no recorte: numa secretaria que nunca
            usou o recurso, uma rosca de uma fatia só parece defeito. */}
        {temDemandaReprimida && (
          <CartaoGrafico
            titulo="Por origem"
            descricao="Quanto do volume veio do estoque anterior ao KAVI."
          >
            <GraficoRosca
              unidade="solicitações"
              fatias={data.porOrigem.map((linha) => ({
                chave: linha.origem,
                rotulo: ROTULOS_ORIGEM[linha.origem],
                valor: linha.total,
              }))}
            />
          </CartaoGrafico>
        )}
      </div>
    </div>
  );
}
